import { getToken, clearToken } from '../utils/authToken';
/**
 * Panel de Resolución por Pasos - DamyxIA
 * Versión simplificada para evitar bug de babel-metadata-plugin
 */

import React, { useState, useRef, useEffect } from 'react';
import { API_URL, API } from '../config';
import { X, Send, Loader2, Check, TrendingUp, Zap, Code2, Wrench, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';


const LEVELS = [
  { id: 'principiante', label: 'Principiante', icon: '🌱', color: 'emerald' },
  { id: 'intermedio', label: 'Intermedio', icon: '⚡', color: 'amber' },
  { id: 'avanzado', label: 'Avanzado', icon: '🔥', color: 'rose' }
];

const PROJECT_TYPES = [
  { id: 'sitio_web', label: 'Sitio Web' },
  { id: 'app_web', label: 'App Web' },
  { id: 'api_backend', label: 'API Backend' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'app_ia', label: 'App con IA' },
  { id: 'fullstack', label: 'Full-stack' }
];

const TECHNOLOGIES = [
  { id: 'react', label: 'React' },
  { id: 'nextjs', label: 'Next.js' },
  { id: 'fastapi', label: 'FastAPI' },
  { id: 'nodejs', label: 'Node.js' },
  { id: 'postgresql', label: 'PostgreSQL' },
  { id: 'mongodb', label: 'MongoDB' },
  { id: 'tailwind', label: 'TailwindCSS' },
  { id: 'docker', label: 'Docker' }
];

const STEPS = [
  { num: 1, emoji: '🧠', title: 'Análisis del pedido' },
  { num: 2, emoji: '📋', title: 'Plan y organización' },
  { num: 3, emoji: '💡', title: 'Explicación' },
  { num: 4, emoji: '🔍', title: 'Revisión y Corrección' },
  { num: 5, emoji: '🚀', title: 'Resultado' },
  { num: 6, emoji: '🔧', title: 'Diagnóstico y Reparación' }
];

const StepResolutionPanel = ({ onClose }) => {
  const { language } = useLanguage();
  const [level, setLevel] = useState('principiante');
  const [projectType, setProjectType] = useState('');
  const [selectedTechs, setSelectedTechs] = useState([]);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [reviewInfo, setReviewInfo] = useState(null);
  const [response, setResponse] = useState('');
  const [isDiagnosticMode, setIsDiagnosticMode] = useState(false);
  const [showTestInvitation, setShowTestInvitation] = useState(false);
  
  // Estados para métricas
  const [metrics, setMetrics] = useState(null);
  const [showMetrics, setShowMetrics] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const chatEndRef = useRef(null);

  // Detectar si el mensaje es un reporte de error
  const isErrorReport = (msg) => {
    const errorKeywords = [
      'no funciona', 'hay un error', 'está roto', 'no carga', 'no se ve',
      'no aparece', 'pantalla en blanco', 'error', 'undefined', 'null',
      'no hace', 'no guarda', 'no actualiza', 'el botón no', 'no responde',
      'falla', 'broken', 'bug', 'issue', 'problem', 'not working'
    ];
    return errorKeywords.some(kw => msg.toLowerCase().includes(kw));
  };

  // Cargar métricas de productividad
  useEffect(() => {
    const fetchMetrics = async () => {
      const token = getToken();
      if (!token || token === 'guest') {
        setLoadingMetrics(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/metrics/productivity?period=month`, {
        });
        if (res.data.success) {
          setMetrics(res.data);
        }
      } catch (err) {
      } finally {
        setLoadingMetrics(false);
      }
    };
    
    fetchMetrics();
  }, [response]); // Recargar métricas después de cada respuesta

  useEffect(() => {
    if (!projectType) return;
    const techMap = {
      principiante: ['react', 'fastapi', 'mongodb', 'tailwind'],
      intermedio: ['nextjs', 'fastapi', 'postgresql', 'tailwind'],
      avanzado: ['nextjs', 'fastapi', 'postgresql', 'tailwind', 'docker']
    };
    setSelectedTechs(techMap[level] || []);
  }, [level, projectType]);

  const toggleTech = (id) => {
    setSelectedTechs(prev => 
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;
    
    const userMsg = message.trim();
    const reportingError = isErrorReport(userMsg);
    
    setMessage('');
    setIsLoading(true);
    setResponse('');
    setShowTestInvitation(false);
    
    // Si es reporte de error, activar modo diagnóstico
    if (reportingError) {
      setIsDiagnosticMode(true);
      setCurrentStep(6); // Paso de diagnóstico
    } else {
      setIsDiagnosticMode(false);
      setCurrentStep(1);
    }

    const contextMsg = reportingError ? `
[MODO DIAGNÓSTICO ACTIVADO]
El usuario reporta un error:
"${userMsg}"

Por favor:
1. Analiza el error
2. Identifica la causa
3. Corrige el código
4. Explica qué corregiste
5. Entrega código completo y funcional

Termina con: "Puedes probarlo ahora. Si hay otro error, dímelo."
    `.trim() : `
[PANEL DE RESOLUCIÓN]
Nivel: ${level}
Proyecto: ${projectType || 'General'}
Tecnologías: ${selectedTechs.join(', ') || 'Auto'}

[SOLICITUD]
${userMsg}

Responde de forma estructurada.
Al final, agrega: "✅ Puedes probarlo ahora. Si encuentras algún error, dímelo y lo reparo automáticamente."
    `.trim();

    try {
      // Simular progreso según el modo
      if (reportingError) {
        // Modo diagnóstico - solo paso 6
        setTimeout(() => setCurrentStep(6), 500);
      } else {
        // Modo normal
        setTimeout(() => setCurrentStep(2), 800);
        setTimeout(() => setCurrentStep(3), 1600);
        setTimeout(() => setCurrentStep(4), 2400);
      }

      const token = getToken();
      const headers = {};  // P2.1 cookie-only: cookie httpOnly via axios.defaults
      
      const res = await axios.post(`${API}/chat`, {
        message: contextMsg,
        conversation_id: null
      }, { headers });

      // Guardar información de revisión si existe
      if (res.data.code_reviewed) {
        setReviewInfo({
          reviewed: true,
          corrections: res.data.corrections_applied || 0,
          summary: res.data.review_summary || '✓ Código revisado'
        });
      }

      setResponse(res.data.response);
      
      if (reportingError) {
        // Mantener en modo diagnóstico completado
        setCurrentStep(6);
      } else {
        setTimeout(() => {
          setCurrentStep(5);
          setShowTestInvitation(true);
        }, 500);
      }
      
    } catch (err) {
      toast.error('Error al procesar');
      setCurrentStep(0);
      setReviewInfo(null);
      setIsDiagnosticMode(false);
    } finally {
      setIsLoading(false);
    }
  };


  const levelColor = LEVELS.find(l => l.id === level)?.color || 'emerald';
  const badgeClass = {
    emerald: 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30',
    amber: 'bg-amber-600/20 text-amber-400 border-amber-500/30',
    rose: 'bg-rose-600/20 text-rose-400 border-rose-500/30'
  }[levelColor];

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/95 backdrop-blur-xl flex flex-col" data-testid="step-resolution-panel">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              ✨ DamyxIA — Resolución Inteligente por Pasos
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              {isDiagnosticMode 
                ? '🔧 Modo Diagnóstico y Reparación Activado'
                : 'Analizo. Organizo. Explico. Ejecuto. Entrego.'
              }
            </p>
          </div>
          <div className="flex items-center gap-4">
            {isDiagnosticMode && (
              <span className="px-3 py-1 rounded-full text-sm border bg-amber-600/20 text-amber-400 border-amber-500/30">
                🔧 Reparando
              </span>
            )}
            <span className={`px-3 py-1 rounded-full text-sm border ${badgeClass}`}>
              {LEVELS.find(l => l.id === level)?.icon} {LEVELS.find(l => l.id === level)?.label}
            </span>
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg" data-testid="close-panel-btn">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Centro - Pasos y Respuesta */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-4">
            {/* Steps Grid - Mostrar solo los pasos relevantes */}
            <div className="grid gap-3">
              {STEPS.filter(step => {
                // En modo diagnóstico, solo mostrar paso 6
                if (isDiagnosticMode) return step.num === 6;
                // En modo normal, mostrar pasos 1-5
                return step.num <= 5;
              }).map(step => {
                const isActive = currentStep === step.num;
                const isCompleted = currentStep > step.num && step.num < 6;
                const isReviewStep = step.num === 4;
                const isDiagnosticStep = step.num === 6;
                return (
                  <div
                    key={step.num}
                    className={`p-4 rounded-xl border transition-all ${
                      isDiagnosticStep && isDiagnosticMode 
                        ? 'bg-amber-600/20 border-amber-500/50' :
                      isActive ? 'bg-indigo-600/20 border-indigo-500/50' :
                      isCompleted ? 'bg-zinc-800/50 border-zinc-700/50' :
                      'bg-zinc-900/30 border-zinc-800/30 opacity-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${
                        isDiagnosticStep && isDiagnosticMode
                          ? 'bg-amber-600 text-white' :
                        isActive ? 'bg-indigo-600 text-white' :
                        isCompleted ? 'bg-emerald-600 text-white' :
                        'bg-zinc-800 text-zinc-500'
                      }`}>
                        {isCompleted && !isDiagnosticStep ? <Check className="w-5 h-5" /> : step.emoji}
                      </div>
                      <div className="flex-1">
                        <span className="text-xs text-zinc-500 uppercase">
                          {isDiagnosticStep ? 'Modo Especial' : `Paso ${step.num}`}
                        </span>
                        <h3 className="text-white font-medium">{step.title}</h3>
                        {/* Mostrar info de revisión en Paso 4 cuando está completado */}
                        {isReviewStep && isCompleted && reviewInfo && (
                          <p className="text-xs text-emerald-400 mt-1">
                            {reviewInfo.corrections > 0 
                              ? `✓ ${reviewInfo.corrections} correcciones aplicadas automáticamente`
                              : '✓ Código limpio, sin errores detectados'
                            }
                          </p>
                        )}
                      </div>
                      {isActive && <Loader2 className="w-5 h-5 text-indigo-400 animate-spin ml-auto" />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Respuesta */}
            {response && (
              <div className="mt-6 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                <h3 className="text-white font-medium mb-3">📋 Respuesta de DamyxIA</h3>
                <div className="text-zinc-300 text-sm whitespace-pre-wrap max-h-96 overflow-y-auto">
                  {response}
                </div>
              </div>
            )}

            {/* Botones de Acción */}
            {currentStep >= 5 && (
              <div className="p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
                <h3 className="text-white font-medium mb-3">🚀 Próximos pasos</h3>
                <div className="flex flex-wrap gap-2">
                  {['Generar componente', 'Agregar backend', 'Base de datos', 'Integrar API', 'Continuar'].map((action, i) => (
                    <button
                      key={i}
                      onClick={() => setMessage(action)}
                      className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm rounded-lg"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Sidebar - Derecha */}
        <aside className="w-72 border-l border-zinc-800 p-4 overflow-y-auto bg-zinc-900/30">
          {/* Nivel */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-zinc-400 uppercase mb-3">👤 Tu Nivel</h3>
            <div className="space-y-2">
              {LEVELS.map(l => (
                <button
                  key={l.id}
                  onClick={() => setLevel(l.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                    level === l.id
                      ? 'bg-indigo-600/20 border border-indigo-500/50 text-white'
                      : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:bg-zinc-800'
                  }`}
                >
                  <span>{l.icon}</span>
                  <span>{l.label}</span>
                  {level === l.id && <Check className="w-4 h-4 ml-auto text-indigo-400" />}
                </button>
              ))}
            </div>
          </div>

          {/* Tipo de Proyecto */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-zinc-400 uppercase mb-3">📁 Proyecto</h3>
            <div className="grid grid-cols-2 gap-2">
              {PROJECT_TYPES.map(p => (
                <button
                  key={p.id}
                  onClick={() => setProjectType(p.id)}
                  className={`p-2 rounded-lg text-xs transition-all ${
                    projectType === p.id
                      ? 'bg-indigo-600/20 border border-indigo-500/50 text-white'
                      : 'bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 hover:bg-zinc-800'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tecnologías */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-zinc-400 uppercase mb-3">💻 Tecnologías</h3>
            <p className="text-xs text-zinc-500 mb-2">
              {selectedTechs.length === 0 ? 'Auto-selección según nivel' : 'Seleccionadas:'}
            </p>
            <div className="flex flex-wrap gap-1">
              {TECHNOLOGIES.map(t => (
                <button
                  key={t.id}
                  onClick={() => toggleTech(t.id)}
                  className={`px-2 py-1 rounded-full text-xs transition-all ${
                    selectedTechs.includes(t.id)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Info Stack */}
          {projectType && (
            <div className="p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
              <h4 className="text-sm font-medium text-white mb-2">Stack Recomendado</h4>
              <div className="flex flex-wrap gap-1">
                {selectedTechs.map(id => {
                  const tech = TECHNOLOGIES.find(t => t.id === id);
                  return tech ? (
                    <span key={id} className="px-2 py-0.5 bg-indigo-600/30 text-indigo-300 rounded text-xs">
                      {tech.label}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {/* Métricas de Productividad - Módulo Discreto */}
          {!loadingMetrics && metrics && (
            <div className="mt-6" data-testid="productivity-metrics">
              <button
                onClick={() => setShowMetrics(!showMetrics)}
                className="w-full flex items-center justify-between text-sm font-medium text-zinc-400 uppercase mb-3 hover:text-zinc-300"
              >
                <span className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Tu Progreso
                </span>
                {showMetrics ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {showMetrics && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* Mensajes motivacionales */}
                  {metrics.metrics?.motivational_messages?.map((msg, i) => (
                    <div
                      key={i}
                      className="p-3 bg-gradient-to-r from-indigo-600/10 to-purple-600/10 rounded-lg border border-indigo-500/20"
                    >
                      <p className="text-sm text-indigo-200">{msg}</p>
                    </div>
                  ))}
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {metrics.metrics?.code_generated > 0 && (
                      <div className="p-2 bg-zinc-800/50 rounded-lg border border-zinc-700/30 text-center">
                        <Code2 className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                        <span className="text-lg font-bold text-white">{metrics.metrics.code_generated}</span>
                        <p className="text-xs text-zinc-500">Componentes</p>
                      </div>
                    )}
                    
                    {metrics.metrics?.errors_fixed > 0 && (
                      <div className="p-2 bg-zinc-800/50 rounded-lg border border-zinc-700/30 text-center">
                        <Wrench className="w-4 h-4 text-amber-400 mx-auto mb-1" />
                        <span className="text-lg font-bold text-white">{metrics.metrics.errors_fixed}</span>
                        <p className="text-xs text-zinc-500">Corregidos</p>
                      </div>
                    )}
                    
                    {metrics.metrics?.diagnostics_completed > 0 && (
                      <div className="p-2 bg-zinc-800/50 rounded-lg border border-zinc-700/30 text-center">
                        <Zap className="w-4 h-4 text-purple-400 mx-auto mb-1" />
                        <span className="text-lg font-bold text-white">{metrics.metrics.diagnostics_completed}</span>
                        <p className="text-xs text-zinc-500">Diagnósticos</p>
                      </div>
                    )}
                    
                    {metrics.streak?.current_streak > 0 && (
                      <div className="p-2 bg-zinc-800/50 rounded-lg border border-zinc-700/30 text-center">
                        <span className="text-lg">🔥</span>
                        <span className="text-lg font-bold text-white ml-1">{metrics.streak.current_streak}</span>
                        <p className="text-xs text-zinc-500">Días seguidos</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Período */}
                  <p className="text-xs text-zinc-600 text-center mt-2">
                    {metrics.metrics?.period_label || 'este mes'}
                  </p>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {/* Input */}
      <footer className="border-t border-zinc-800 p-4 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder={language === 'es' ? 'Describe qué quieres crear...' : 'Describe what you want to create...'}
            className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={isLoading}
            data-testid="step-panel-input"
          />
          <button
            onClick={handleSend}
            disabled={isLoading || !message.trim()}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 text-white rounded-xl flex items-center gap-2"
            data-testid="step-panel-send"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5" /> Enviar</>}
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-2 text-center">
          DamyxIA te guiará paso a paso
        </p>
      </footer>
    </div>
  );
};

export default StepResolutionPanel;
